import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { authAPI } from '../utils/api';

const AccountSettingsModal = ({ isOpen, onClose }) => {
    const { logout } = useAuth();
    const { showSuccess, showError } = useNotification();
    const [activeTab, setActiveTab] = useState('password'); // 'password' or 'delete'
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Password Form State
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    // Delete Account Form State
    const [deleteConfirmation, setDeleteConfirmation] = useState('');

    if (!isOpen) return null;

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (passwordData.newPassword.length < 8) {
            return showError('New password must be at least 8 characters');
        }

        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            return showError('New passwords do not match');
        }

        setLoading(true);
        try {
            const res = await authAPI.changePassword({
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            });

            if (res.data?.success) {
                showSuccess('Password updated successfully');
                setPasswordData({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
                onClose();
            } else {
                showError(res.data?.message || 'Failed to update password');
            }
        } catch (err) {
            showError(err.response?.data?.message || 'Error updating password');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();

        if (deleteConfirmation !== 'DELETE') {
            return showError('Please type DELETE exactly to confirm');
        }

        setLoading(true);
        try {
            const res = await authAPI.deleteAccount({ confirmation: deleteConfirmation });
            if (res.data?.success) {
                showSuccess('Account successfully soft-deleted. Logging out...');
                setTimeout(() => {
                    logout();
                }, 1500);
            } else {
                showError(res.data?.message || 'Failed to delete account');
            }
        } catch (err) {
            showError(err.response?.data?.message || 'Error deleting account');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700 overflow-hidden shadow-2xl relative">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-700">
                    <h2 className="text-xl font-bold text-white">Account Settings</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-700">
                    <button
                        className={`flex-1 py-3 font-medium transition-colors ${activeTab === 'password' ? 'text-gold border-b-2 border-gold bg-gray-700/30' : 'text-gray-400 hover:text-gray-200'}`}
                        onClick={() => setActiveTab('password')}
                    >
                        Change Password
                    </button>
                    <button
                        className={`flex-1 py-3 font-medium transition-colors ${activeTab === 'delete' ? 'text-red-500 border-b-2 border-red-500 bg-red-900/10' : 'text-gray-400 hover:text-gray-200'}`}
                        onClick={() => setActiveTab('delete')}
                    >
                        Delete Account
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {activeTab === 'password' ? (
                        <form onSubmit={handlePasswordChange} className="space-y-4">
                            <div>
                                <label className="block text-gray-400 mb-1 text-sm">Current Password</label>
                                <div className="relative">
                                    <input
                                        type={showOldPassword ? "text" : "password"}
                                        value={passwordData.oldPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 pr-10 text-white focus:ring-1 focus:ring-gold outline-none"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none"
                                    >
                                        {showOldPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-gray-400 mb-1 text-sm">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        value={passwordData.newPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 pr-10 text-white focus:ring-1 focus:ring-gold outline-none"
                                        required
                                        minLength="8"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none"
                                    >
                                        {showNewPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-gray-400 mb-1 text-sm">Confirm New Password</label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={passwordData.confirmNewPassword}
                                        onChange={(e) => setPasswordData({ ...passwordData, confirmNewPassword: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 pr-10 text-white focus:ring-1 focus:ring-gold outline-none"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none"
                                    >
                                        {showConfirmPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs text-gray-500 italic mb-4">Note: If you signed up with Google, you cannot change your password here.</p>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gold text-dark font-bold py-3 rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 mt-4"
                            >
                                {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleDeleteAccount} className="space-y-4">
                            <div className="bg-red-900/30 border border-red-900/50 rounded-lg p-4 mb-4">
                                <h3 className="text-red-400 font-bold mb-2">Danger Zone</h3>
                                <p className="text-gray-300 text-sm">
                                    Deleting your account is a permanent action. All your active subscriptions will be cancelled immediately and cannot be recovered.
                                </p>
                            </div>

                            <div>
                                <label className="block text-gray-400 mb-2 text-sm">To confirm, please type <b>DELETE</b> below:</label>
                                <input
                                    type="text"
                                    value={deleteConfirmation}
                                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                                    placeholder="DELETE"
                                    className="w-full bg-gray-700 border border-red-900/50 focus:border-red-500 rounded-lg p-3 text-white focus:ring-1 focus:ring-red-500 outline-none uppercase placeholder:text-gray-600"
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading || deleteConfirmation !== 'DELETE'}
                                className="w-full bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 mt-4"
                            >
                                {loading ? 'Processing...' : 'Permanently Delete Account'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountSettingsModal;
